// API Request Cache and Deduplication Utility
const apiCache = new Map<string, { data: any; timestamp: number }>();
const pendingRequests = new Map<string, Promise<any>>();

export const cachedApiCall = async <T>(
  key: string,
  apiCall: () => Promise<T>,
  ttl: number = 30000 // 30 seconds default TTL
): Promise<T> => {
  // Check cache
  if (apiCache.has(key)) {
    const cached = apiCache.get(key)!;
    if (Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }
  }

  // Check pending requests to avoid duplicate calls
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key)!;
  }

  // Make request
  const promise = apiCall();
  pendingRequests.set(key, promise);

  try {
    const result = await promise;
    apiCache.set(key, { data: result, timestamp: Date.now() });
    return result;
  } finally {
    pendingRequests.delete(key);
  }
};

export const clearApiCache = (keyPrefix?: string) => {
  if (keyPrefix) {
    // Clear specific cache entries by prefix
    const keys = Array.from(apiCache.keys());
    for (const key of keys) {
      if (key.startsWith(keyPrefix)) {
        apiCache.delete(key);
      }
    }
  } else {
    // Clear all cache
    apiCache.clear();
  }
  pendingRequests.clear();
};

export const invalidateUserCache = () => {
  clearApiCache("user_");
};
